/**
 * Keeps the cart drawer and Rebuy widgets in sync with cart updates.
 *
 * The drawer is morphed from Section Rendering API HTML, which contains an empty
 * `[data-rebuy-id]` container. Morphing inserts a new container or wipes the one
 * Rebuy already filled, and Rebuy only scans the page once at load.
 *
 * Rebuy adds products through its own cart requests, which the theme never sees,
 * so Rebuy cart events are translated into a theme cart lines update event.
 */

import { CartLinesUpdateEvent } from "@shopify/events";

const DRAWER_SELECTOR = "#cart-drawer";
const WIDGET_SELECTOR = "[data-rebuy-id]";
const DEBOUNCE_MS = 50;
const REBUY_CART_EVENTS = ["rebuy:cart.add", "rebuy:cart.change"];
const CART_SYNC_DEBOUNCE_MS = 150;

/** @type {number | undefined} */
let timeout;

/** @type {number | undefined} */
let cartSyncTimeout;

async function syncCartFromRebuy() {
  const target = document.querySelector("cart-drawer-component") ?? document;
  const deferred = CartLinesUpdateEvent.createPromise();

  try {
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: "application/json" },
    });
    const ajaxCart = await response.json();
    const lines = (ajaxCart.items ?? []).map(
      /** @param {{ key: string, quantity: number }} item */ (item) => ({
        id: item.key,
        quantity: item.quantity,
      }),
    );
    if (lines.length === 0) return;

    // Dispatch before resolving so listeners attach to the promise first.
    target.dispatchEvent(
      new CartLinesUpdateEvent({
        action: "update",
        context: "cart",
        lines,
        promise: deferred.promise,
      }),
    );

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        items: ajaxCart.items,
        itemCount: ajaxCart.item_count,
        source: "rebuy",
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
    console.warn("[ln-rebuy] Cart sync failed:", error);
  }
}

function scheduleCartSync() {
  clearTimeout(cartSyncTimeout);
  cartSyncTimeout = window.setTimeout(syncCartFromRebuy, CART_SYNC_DEBOUNCE_MS);
}

for (const eventName of REBUY_CART_EVENTS) {
  document.addEventListener(eventName, scheduleCartSync);
}

function initEmptyWidgets() {
  const rebuy = /** @type {{ Rebuy?: { init?: () => void } }} */ (
    /** @type {unknown} */ (window)
  ).Rebuy;
  if (typeof rebuy?.init !== "function") return;

  const hasEmptyWidget = Array.from(
    document.querySelectorAll(`${DRAWER_SELECTOR} ${WIDGET_SELECTOR}`),
  ).some((widget) => widget.childElementCount === 0);

  if (hasEmptyWidget) rebuy.init();
}

/** @param {Node} node */
function isWidgetNode(node) {
  return (
    node instanceof Element &&
    (node.matches(WIDGET_SELECTOR) ||
      node.querySelector(WIDGET_SELECTOR) !== null)
  );
}

/** @param {MutationRecord[]} records */
function handleMutations(records) {
  const needsInit = records.some((record) => {
    if (Array.from(record.addedNodes).some(isWidgetNode)) return true;

    const target =
      record.target instanceof Element
        ? record.target.closest(WIDGET_SELECTOR)
        : null;
    return target !== null && record.removedNodes.length > 0;
  });

  if (!needsInit) return;

  clearTimeout(timeout);
  timeout = window.setTimeout(initEmptyWidgets, DEBOUNCE_MS);
}

const drawer = document.querySelector(DRAWER_SELECTOR);

if (drawer) {
  new MutationObserver(handleMutations).observe(drawer, {
    childList: true,
    subtree: true,
  });
}
